/**
 * @fileoverview localStorage utilities for referral tracking
 * @module features/referrals/utils/referralStorage
 *
 * Manages referral data in browser localStorage before wallet connection.
 * Implements hybrid storage strategy:
 * 1. User clicks referral link → Save to localStorage
 * 2. User connects wallet → Migrate to database
 * 3. Clear localStorage after successful migration
 *
 * Storage Priority (when getting referral):
 * 1. Database (by wallet address) - most reliable
 * 2. localStorage - fallback before wallet connect
 * 3. URL parameter - fallback if nothing stored
 */

/**
 * localStorage key for referral data.
 * Format: 'onicorn_referral'
 */
const REFERRAL_KEY = 'onicorn_referral';

/**
 * Referral data stored in localStorage.
 */
export interface StoredReferral {
  /** KOL slug from referral link (e.g., 'crypto_expert') */
  kolSlug: string;
  /** Source of the referral (e.g., 'link', 'qr_code') */
  source: string;
  /** Timestamp when referral was first saved */
  timestamp: string;
}

/**
 * Saves referral data to localStorage.
 *
 * Called when user clicks a referral link but hasn't connected wallet yet.
 * Overwrites previous referral (last-click attribution).
 *
 * @param kolSlug - KOL slug from URL parameter
 * @param source - Referral source (default: 'link')
 *
 * @example
 * ```typescript
 * // User visits: /deals/project-name?ref=crypto_expert
 * const refParam = searchParams.get('ref');
 * if (refParam) {
 *   saveReferral(refParam);
 * }
 * ```
 */
export function saveReferral(kolSlug: string, source: string = 'link'): void {
  try {
    const referralData: StoredReferral = {
      kolSlug,
      source,
      timestamp: new Date().toISOString(),
    };

    localStorage.setItem(REFERRAL_KEY, JSON.stringify(referralData));

  } catch (error) {
    console.error('[Referral] localStorage operation failed:', error);
  }
}

/**
 * Gets stored referral data from localStorage.
 *
 * Returns null if no referral stored or data is invalid.
 * Used as fallback when wallet not connected or no database attribution exists.
 *
 * @returns Stored referral data or null
 *
 * @example
 * ```typescript
 * const stored = getStoredReferral();
 * if (stored) {
 *   console.log(`Referred by: ${stored.kolSlug}`);
 * }
 * ```
 */
export function getStoredReferral(): StoredReferral | null {
  try {
    const stored = localStorage.getItem(REFERRAL_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as StoredReferral;

    // Validate required fields
    if (!parsed.kolSlug || !parsed.timestamp) {
      clearReferral();
      return null;
    }

    return parsed;
  } catch (error) {
    console.error('[Referral] localStorage operation failed:', error);
    return null;
  }
}

/**
 * Clears referral data from localStorage.
 *
 * Called after successfully migrating attribution to database.
 * Also called if invalid data detected.
 *
 * @example
 * ```typescript
 * // After saving to database
 * await saveWalletAttribution({ ... });
 * clearReferral(); // Clean up localStorage
 * ```
 */
export function clearReferral(): void {
  try {
    localStorage.removeItem(REFERRAL_KEY);
  } catch (error) {
    console.error('[Referral] localStorage operation failed:', error);
  }
}

/**
 * Checks if a referral is currently stored.
 *
 * Quick check without parsing the full data.
 *
 * @returns true if referral exists in localStorage
 *
 * @example
 * ```typescript
 * if (hasStoredReferral()) {
 *   // Show UI indicator that user came from referral
 * }
 * ```
 */
export function hasStoredReferral(): boolean {
  try {
    return localStorage.getItem(REFERRAL_KEY) !== null;
  } catch (error) {
    console.error('[Referral] localStorage operation failed:', error);
    return false;
  }
}

/**
 * Gets the KOL slug from stored referral (convenience method).
 *
 * @returns KOL slug or null
 *
 * @example
 * ```typescript
 * const kolSlug = getStoredKOLSlug();
 * if (kolSlug) {
 *   const kol = await getKOLBySlug(kolSlug);
 * }
 * ```
 */
export function getStoredKOLSlug(): string | null {
  const referral = getStoredReferral();
  return referral?.kolSlug || null;
}
